import { BaseApi } from "../utils/request";

export type KycType =
  | "BR_KYC"
  | "EC_KYC"
  | "PE_KYC"
  | "CL_KYC"
  | "CO_KYC"
  | "PH_KYC";

export interface KycData {
  id?: string;
  created?: number;
  modified?: number;
  countryId?: string;
  abFlag?: boolean;
  idType?: string;
  idNo?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  status?: string;
  manualAudit?: boolean;
  fullName?: string;
  birthDay?: string | number; // 生日字段支持字符串或时间戳
  idFile?: string; // ID照片base64
  userIdFile?: string; // 手持ID照片base64
  middleName?: string;
  birthAddress?: string;
  currentAddress?: string;
  permanentAddress?: string;
  gender?: string;
  nationality?: string;
  incomeSource?: string;
  workNature?: string;
  phone?: string;
}

export interface KycResponse {
  code: number;
  message: string;
  data: KycData | null;
}

export interface KycStatusInfo {
  id?: string;
  countryId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  idType?: string;
  idNo?: string;
  status?: string;
  created?: string;
  modified?: string;
}

export interface KycStatusModel {
  hasKyc: boolean;
  verified: boolean;
  status: string;
  auditMessage: string | null;
  auditTime: string | null;
  kycInfo: KycStatusInfo | null;
}

export interface KycStatusResponse {
  code: number;
  message: string;
  data: KycStatusModel | null;
}

export interface WithdrawKycGuideModel {
  needKycGuide: boolean;
  successWithdrawCount: number;
  withdrawGuideCount: number;
  kycPassed: boolean;
}

export interface WithdrawKycGuideResponse {
  code: number;
  message: string;
  data: WithdrawKycGuideModel | null;
}

/**
 * KYC API 类
 * 继承 BaseApi，提供 KYC 相关的 API 接口
 */
export class KycApi extends BaseApi {
  constructor() {
    super();
  }

  /**
   * 获取 KYC 信息
   * @returns Promise<KycResponse>
   */
  async getKycInfo(): Promise<KycResponse> {
    const response = await this.get<any>("/api/kyc/one");

    // 从响应中提取数据，尝试多种可能的路径
    let kycData = null;

    // 根据 BaseApi 的响应格式，数据可能在这些位置
    if (response.data?.model) {
      kycData = response.data.model;
    } else if (response.model) {
      kycData = response.model;
    } else if (response.data) {
      kycData = response.data;
    }

    // 从响应中获取状态码
    // 后端返回的是 {status: 0, ...}，优先使用 data.status
    const statusCode = response.data?.status ?? response.status ?? 0;

    const result = {
      code: statusCode,
      message: response.message || response.data?.message || "",
      data: kycData,
    };

    return result;
  }

  /**
   * 获取 KYC 状态
   * @returns Promise<KycStatusResponse>
   */
  async getKycStatus(): Promise<KycStatusResponse> {
    const response = await this.post<any>("/api/kyc/status", {});

    // 提取数据
    const statusData =
      response.data?.model || response.model || response.data || null;

    // 从响应中获取状态码
    const statusCode = response.data?.status ?? response.status ?? 0;

    return {
      code: statusCode,
      message: response.message || response.data?.message || "",
      data: statusData,
    };
  }

  /**
   * 保存/更新 KYC 信息
   * @param data KYC 数据
   * @returns Promise<KycResponse>
   */
  async saveKycInfo(data: KycData): Promise<KycResponse> {
    const response = await this.post<any>("/api/kyc/persist", data);

    // 提取数据
    const resultData =
      response.data?.model || response.model || response.data || null;

    // 从响应中获取状态码，优先使用 data.status
    const statusCode = response.data?.status ?? response.status ?? 0;

    return {
      code: statusCode,
      message: response.message || response.data?.message || "",
      data: resultData,
    };
  }

  async getWithdrawKycGuide(): Promise<WithdrawKycGuideResponse> {
    const response = await this.get<any>("/api/pay/withdraw_kyc_guide");
    const model =
      response.data?.model || response.model || response.data || null;
    const statusCode = response.data?.status ?? response.status ?? 0;
    return {
      code: statusCode,
      message: response.message || response.data?.message || "",
      data: model,
    };
  }
}

export const kycApi = new KycApi();

// 导出便捷方法
export const getKycInfo = () => kycApi.getKycInfo();
export const getKycStatus = () => kycApi.getKycStatus();
export const saveKycInfo = (data: KycData) => kycApi.saveKycInfo(data);
export const getWithdrawKycGuide = () => kycApi.getWithdrawKycGuide();
